import { lastCallFromProjection } from '@revcall/shared';

/**
 * ENG-5: the browser only loads part of the call history (paged, capped), so
 * the « last call » of a number could be missing or stale, and canCall would
 * redial a contact already reached. Each ProspectQueue record now carries
 * the server's projection of its full history in `call_history` (shared
 * prospect-call-projection.ts; not the older last_call_at / last_call_status
 * strings). Where it is newer than the call the browser
 * loaded, it takes that call's place; otherwise the loaded call stays (it
 * has every field the screens show). Contacts without a projection (not
 * backfilled yet) keep today's behaviour.
 */
type LoadedCall = { created_date?: string | null; call_outcome?: string | null; call_status?: string | null; meeting_booked?: boolean | null };

export function withProjectedLastCalls<C extends LoadedCall>(
  byPhone: Record<string, C>,
  prospects: ReadonlyArray<{ prospect_phone?: string | null } & Record<string, unknown>>,
): Record<string, C> {
  let out = byPhone;
  for (const p of prospects) {
    const phone = p.prospect_phone;
    if (!phone) continue;
    const projected = lastCallFromProjection((p as { call_history?: Record<string, unknown> | null }).call_history as never);
    if (!projected) continue; // none yet, or never called according to the server
    const loaded = out[phone];
    if (loaded) {
      const lt = new Date(loaded.created_date ?? '').getTime();
      const pt = new Date(String(projected.created_date)).getTime();
      // The loaded call stays when it is newer (the projection lags a write),
      // or when it is the projected call itself. Two calls at the same
      // instant are not the same call: the projection, built from the whole
      // history, settles the tie.
      const same = lt === pt && (loaded.call_outcome ?? null) === (projected.call_outcome ?? null)
        && (loaded.call_status ?? null) === (projected.call_status ?? null) && (loaded.meeting_booked === true) === (projected.meeting_booked === true);
      if (lt > pt || same) continue;
    }
    if (out === byPhone) out = { ...byPhone };
    out[phone] = { ...projected, prospect_phone: phone, projected: true } as unknown as C;
  }
  return out;
}
